<template>
  <div>
    <v-select
      class="hidden-md-and-up mt-4"
      v-model="computedMobileSortModel"
      :items="computedMobileSorts"
      item-text="text"
      item-value="value"
      dense
      rounded
      filled
      hide-details
      prepend-inner-icon="mdi-sort"
    ></v-select>

    <div class="mt-4">
      <v-chip outlined color="success" small class="ml-2" @click="emitSearch($t('states.indexed'))">
        <v-icon small left>mdi-check</v-icon>
        {{ $t('states.indexed') }} {{ computedRows.filter(row => row.found === $t('states.indexed')).length }}
      </v-chip>
      <v-chip outlined color="warning" small class="ml-2" @click="emitSearch($t('protocol-mismatch'))">
        <v-icon small left>mdi-alert</v-icon>
        {{ $t('protocol-mismatch') }} {{ computedRows.filter(row => row.found === $t('protocol-mismatch')).length }}
      </v-chip>
      <v-chip outlined color="error" small class="ml-2" @click="emitSearch($t('states.no-indexed'))">
        <v-icon small left>mdi-close</v-icon>
        {{ $t('states.no-indexed') }} {{ computedRows.filter(row => row.found === $t('states.no-indexed')).length }}
      </v-chip>
      <v-chip outlined color="primary" small class="ml-2">
        <v-icon small left>mdi-chart-arc</v-icon>
        {{ $t('indexation-rate') }}
        {{ Math.round(computedRows.filter(row => getItemFound(row)).length / computedRows.length * 100) }} %
      </v-chip>
    </div>

    <v-data-table
      class="mt-4 table-url-monitors pi-4"
      dense
      :search="search"
      :options.sync="options"
      v-model="rows.selected"
      :headers="computedHeaders"
      :items="computedRows"
      item-key="id"
      :item-class="getRowClass"
      :show-select="$vuetify.breakpoint.mdAndUp"
      :footer-props="{ 'items-per-page-options': [50, 100, 200, 500, 1000] }"
      :mobile-breakpoint="0"
      @click:row="handleClick"
    >
      <template v-slot:group.header="{ headers, group }">
        <td :colspan="$vuetify.breakpoint.mdAndUp ? 2 : 1" class="text-start" @click="emitSearch(group)">
          <v-simple-checkbox
            v-show="$vuetify.breakpoint.mdAndUp"
            :indeterminate="getGroupCheckboxStatus(group) === null"
            :value="getGroupCheckboxStatus(group)"
            style="display: inline; top: 4px; margin-right: 6px;"
            @click="handleGroupCheckboxClick(group)">
          </v-simple-checkbox>
          <span style="line-height: 32px; " class="font-weight-bold">{{ group }}</span>
        </td>
        <td :colspan="1" class="text-center" @click="emitSearch(group)">
          <span class="success--text mr-1"><v-icon color="success" small>mdi-check</v-icon> {{
              getWebsiteMonitorsCount(group, 'found')
            }}</span>
          <span class="error--text"><v-icon color="error" small>mdi-close</v-icon> {{
              getWebsiteMonitorsCount(group, 'not-found')
            }}</span>
        </td>
        <td :colspan="headers.length - 3 + ($vuetify.breakpoint.mdAndUp ? 0 : 1)" @click="emitSearch(group)"></td>
      </template>
      <template v-slot:item.url="{ item }">
        <a-pretty-url :url="item.url" mobile="hide-path"></a-pretty-url>
      </template>
      <template v-slot:item.found="{ item }">
        <v-icon color="primary" v-if="getItemFound(item) === null">mdi-loading mdi-spin mdi-anim-1s</v-icon>
        <div v-else>
          <div v-if="getItemFlags(item).protocol_mismatch">
            <v-tooltip bottom max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <v-chip small color="warning" v-bind="attrs" v-on="on" dark @click="emitSearch($t('protocol-mismatch'))">
                  {{ $t('protocol-mismatch') }}
                </v-chip>
              </template>
              {{ $t('protocol-mismatch-description') }}
            </v-tooltip>
          </div>
          <url-found v-else :value="getItemFound(item)" @click="emitSearch"></url-found>
        </div>
      </template>
      <template v-slot:item.done_at="{ item }">
        <span v-if="getItemDoneAt(item)" :key="item.id + sinceKey">
          {{ $moment(getItemDoneAt(item)).fromNow() }}
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          icon color="primary"
          target="_blank"
          :href="'https://www.google.com/search?q=site%3A' + encodeURIComponent(item.url)">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <v-btn
          small
          icon color="primary"
          target="_blank"
          :disabled="getItemCache(item) === null"
          :href="getItemCache(item)">
          <v-icon>mdi-magnify-scan</v-icon>
        </v-btn>
        <v-btn
          small
          icon color="primary"
          target="_blank"
          :href="item.url">
          <v-icon>mdi-link</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import UrlFound from './Common/UrlFound'
import { get } from 'lodash'

export default {
  name: 'PageTable',
  components: { UrlFound },
  props: ['search', 'filter'],
  data () {
    return {
      persist: ['options'],
      persistId: 'PageTableUrl',
      options: {
        page: 1,
        itemsPerPage: 200,
        sortBy: ['found'],
        sortDesc: [true],
        groupBy: ['domain'],
        groupDesc: [true],
        mustSort: true,
        multiSort: false
      },
      rows: {
        selected: [],
      },
      headers: [
        { visible: null, text: '', value: 'selection' },
        { visible: 'xs', text: this.$t('domain'), value: 'domain' },
        { visible: 'xs', text: this.$t('url'), value: 'url', cellClass:'truncate-text', groupable: false },
        { visible: 'xs', text: this.$t('found'), value: 'found', cellClass:'td-presence', align: 'center', groupable: false },
        { visible: 'xs', text: this.$t('done-at'), value: 'done_at', cellClass:'td-done-at', align: 'center', groupable: false },
        {
          visible: 'xs',
          text: this.$t('actions'),
          value: 'actions',
          cellClass: 'actions',
          align: 'right',
          groupable: false
        }
      ],
      sinceKey: Math.random().toString(36).substr(2, 5),
      sinceKeyInterval: null
    }
  },
  watch: {
    computedSelectedRowsIds (value) {
      this.$emit('select', value)
    }
  },
  computed: {
    reports () {
      return this.$services.urlMonitoring.reports.find('all')
    },
    urlMonitors () {
      return this.$services.urlMonitoring.urlMonitors.all
    },
    computedHeaders () {
      let weights = { 'xs': 1, 'sm': 2, 'md': 3, 'lg': 4, 'xl': 5 }
      return this.headers.filter(header => {
        return weights[header.visible] <= weights[this.$vuetify.breakpoint.name]
      }).map(header => {
        if (Array.isArray(header.text)) {
          return {
            ...header,
            text: header.text[this.$vuetify.breakpoint.name === 'xs' ? 0 : 1]
          }
        } else {
          return header
        }
      })
    },
    computedMobileSorts () {
      const sorts = []
      this.headers.forEach(header => {
        if (header.visible) {
          sorts.push({
            text: (Array.isArray(header.text) ? header.text[1] : header.text) + ' (asc)',
            value: { column: header.value, desc: false }
          })
          sorts.push({
            text: (Array.isArray(header.text) ? header.text[1] : header.text) + ' (desc)',
            value: { column: header.value, desc: true }
          })
        }
      })
      return sorts
    },
    computedMobileSortModel: {
      get () {
        return {
          column: this.options.sortBy[0],
          desc: this.options.sortDesc[0]
        }
      },
      set (value) {
        this.$set(this.options, 'sortBy', [value.column])
        this.$set(this.options, 'sortDesc', [value.desc])
      }
    },
    computedSelectedRowsIds () {
      return this.rows.selected.map(selected => {
        return selected.id
      })
    },
    computedRows () {
      return this.urlMonitors.map(urlMonitor => {
        return {
          ...urlMonitor,
          found: this.getItemFoundColumnText(urlMonitor),
          done_at: this.getItemDoneAt(urlMonitor)
        }
      })
    }
  },
  methods: {
    getItemFoundColumnText(item) {
      if(this.getItemFlags(item).protocol_mismatch) return this.$t('protocol-mismatch')
      else if (this.getItemFound(item)) return this.$t('states.indexed')
      else return this.$t('states.no-indexed')
    },
    getItemFound (item) {
      if (!(item.id in this.reports)) return null
      if (!('found' in this.reports[item.id])) return null
      else return this.reports[item.id].found
    },
    getItemFlags (item) {
      if (!(item.id in this.reports)) return {}
      if (!('flags' in this.reports[item.id])) return {}
      else return this.reports[item.id].flags
    },
    getItemCache (item) {
      return get(this.reports, item.id + '.cache', null)
    },
    getItemDoneAt (item) {
      return get(this.reports, item.id + '.done_at', null)
    },
    getGroupCheckboxStatus (domain) {
      let checked = 0
      let unchecked = 0

      this.computedRows.forEach(row => {
        if (row.domain === domain) {
          const found = this.rows.selected.find(selected => selected.id === row.id)
          if (found) checked++
          else unchecked++
        }
      })

      if (checked > 0 && unchecked > 0) {
        return null
      } else {
        return checked > 0
      }
    },
    handleGroupCheckboxClick (domain) {
      this.computedRows.forEach(row => {
        if (row.domain === domain) {
          const found = this.rows.selected.findIndex(selected => selected.id === row.id)
          if (found === -1) {
            this.rows.selected.push(row)
          } else {
            this.rows.selected.splice(found, 1)
          }
        }
      })
    },
    handleClick (item) {
      console.log(item)
    },
    emitSearch (search) {
      this.$emit('search', search)
    },
    handleCacheClick (item) {
      // TODO ?
    },
    getRowClass (item) {
      if (this.rows.selected.includes(item.id)) {
        return 'v-data-table__expanded__content row-selected'
      } else if (item.position === null) {
        return 'v-data-table__empty-wrapper'
      } else {
        return ''
      }
    },
    getWebsiteMonitorsCount (domain, mode) {
      return this.computedRows.filter(row => {
        return row.domain === domain
      }).reduce((total, row) => {
        if (mode === 'found') return total + (this.getItemFound(row) === 1 ? 1 : 0)
        else return total + (this.getItemFound(row) === 0 ? 1 : 0)
      }, 0)
    },
  },
  mounted () {
    this.$bus.on('url-monitoring/require_refresh', () => this.rows.selected = [])
    this.sinceKeyInterval = setInterval(() => {
      this.sinceKey = Math.random().toString(36).substr(2, 5)
    }, 10 * 1000)
  },
  destroyed () {
    clearInterval(this.sinceKeyInterval)
  }
}
</script>

<style>
/* Try to stack kw smaller in order to use a single table line for two line text */
.table-url-monitors tr > td:first-child {
  line-height: 1em;
}

.table-url-monitors tr td {
  /* white-space: nowrap; */ /* DISABLE KW ON TWO LINES */
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-url-monitors tr.v-row-group__header td {
  cursor: pointer;
}

.table-url-monitors .td-presence, .table-url-monitors .td-done-at {
  width: 10%;
  white-space: nowrap;
}

.table-url-monitors .actions {
  width: 1%;
  white-space: nowrap;
}

@media screen and (max-width: 960px) {
  .table-url-monitors td {
    font-size: 0.800rem !important;
  }
}

@media screen and (max-width: 600px) {
  .table-url-monitors tr td:first-child {
    max-width: 240px;
  }
}

@media screen and (max-width: 400px) {
  .table-url-monitors tr td:first-child {
    max-width: 180px;
  }
}
</style>

<i18n>
{
	"en": {
		"actions": "Stock",
		"domain": "Website",
		"done-at": "Done on",
		"found": "Presence of URL",
		"indexation-rate": "Indexation rate : ",
		"protocol-mismatch": "Protocol mismatch",
		"protocol-mismatch-description": "URL was found but with a different protocol. Did you specify an http link while it is indexed in https ?",
		"url": "URL"
	},
	"es": {
		"actions": "Acciones",
		"domain": "Sitio Web",
		"done-at": "Realizado el",
		"found": "Presencia de URL",
		"indexation-rate": "Tasa de indexación : ",
		"protocol-mismatch": "Error de protocolo",
		"protocol-mismatch-description": "Se encontró una URL pero con un protocolo diferente. ¿Especificó un enlace http mientras está indexado en https?",
		"url": "URL"
	},
	"fr": {
		"actions": "Actions",
		"domain": "Site Web",
		"done-at": "Effectué le",
		"found": "Présence de l'URL",
		"indexation-rate": "Taux indexation : ",
		"protocol-mismatch": "Erreur de protocole",
		"protocol-mismatch-description": "L'URL a été trouvée mais avec un autre protocole. Avez-vous spécifié un lien http alors qu'il est indexé en https ?",
		"url": "URL"
	},
	"it": {
		"actions": "Azioni",
		"domain": "Sito web",
		"done-at": "Effettuato il",
		"found": "Presenza dell'URL",
		"indexation-rate": "Tasso di indicizzazione : ",
		"protocol-mismatch": "Errore di protocollo",
		"protocol-mismatch-description": "L'URL è stato trovato ma con un protocollo diverso. Hai specificato un link http mentre è indicizzato in https?",
		"url": "URL"
	},
	"pt": {
		"actions": "Ações",
		"domain": "Website",
		"done-at": "Efetuado em",
		"found": "Presença do URL",
		"indexation-rate": "Taxa de indexação : ",
		"protocol-mismatch": "Erro de protocolo",
		"protocol-mismatch-description": "O URL foi encontrado mas com um protocolo diferente. Especificou um link http enquanto está indexado em https?",
		"url": "URL"
	},
	"pl": {
		"actions": "Akcje",
		"domain": "Strona internetowa",
		"done-at": "Wykonano",
		"found": "Obecność URL",
		"indexation-rate": "Wskaźnik indeksacji : ",
		"protocol-mismatch": "Niezgodność protokołu",
		"protocol-mismatch-description": "URL został znaleziony, ale z innym protokołem. Czy podałeś link http, podczas gdy jest indeksowany w https?",
		"url": "URL"
	}
}
</i18n>
